import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Building2, 
  Link as LinkIcon, 
  Search,
  Mail,
  Send,
  X,
  MapPin,
  CheckCircle,
  XCircle
} from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const DiveCenterAccountsManager = ({ token }) => {
  const [accounts, setAccounts] = useState([]);
  const [diveCenters, setDiveCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [linkingAccount, setLinkingAccount] = useState(null);
  const [selectedCenterId, setSelectedCenterId] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all dive center accounts
      const accountsRes = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const dcAccounts = accountsRes.data.filter(u => u.role === "dive_center");
      setAccounts(dcAccounts);

      // Fetch all dive centers for linking
      const centersRes = await axios.get(`${API}/dive-centers`);
      setDiveCenters(centersRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setSendingInvite(true);
    try {
      await axios.post(`${API}/admin/send-invite`, {
        email: inviteEmail,
        invitation_type: "dive_center"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Dive center invitation sent!");
      setInviteEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleLinkCenter = async () => {
    if (!selectedCenterId) {
      toast.error("Please select a dive center");
      return;
    }

    if (!linkingAccount?.id) {
      toast.error("No account selected");
      return;
    }

    console.log("Linking account:", linkingAccount.id, "to dive center:", selectedCenterId);

    try {
      const response = await axios.put(
        `${API}/admin/users/${linkingAccount.id}/link-dive-center?dive_center_id=${selectedCenterId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Link response:", response.data);
      toast.success("Account linked to dive center!");
      setLinkingAccount(null);
      setSelectedCenterId("");
      await fetchData();
    } catch (error) {
      console.error("Link error:", error);
      toast.error(error.response?.data?.detail || "Failed to link account");
    }
  };

  const filteredAccounts = accounts.filter(acc => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return acc.name?.toLowerCase().includes(term) || acc.email?.toLowerCase().includes(term);
  });

  const getLinkedCenterName = (centerId) => {
    const center = diveCenters.find(c => c.id === centerId);
    return center?.name || "Unknown";
  };

  return (
    <div className="space-y-6">
      {/* Invite New Dive Center */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="w-5 h-5" />
            Invite Dive Center
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Input
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="dive.center@example.com"
              type="email"
              className="flex-1"
            />
            <Button
              onClick={handleSendInvite}
              disabled={sendingInvite}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingInvite ? "Sending..." : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invite
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            This will send a special invitation that creates a dive center account upon registration.
          </p>
        </CardContent>
      </Card>

      {/* Dive Center Accounts List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5" />
              Dive Center Accounts ({accounts.length})
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading...</p>
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No dive center accounts found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAccounts.map((account) => (
                <div
                  key={account.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-gray-50 gap-3"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold shrink-0">
                      {account.name?.charAt(0) || "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{account.name}</p>
                      <p className="text-sm text-gray-500 truncate">{account.email}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    {/* Linked Status */}
                    {account.linked_dive_center_id ? (
                      <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">
                          Linked to: {getLinkedCenterName(account.linked_dive_center_id)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-orange-600 bg-orange-50 px-3 py-1.5 rounded-full">
                        <XCircle className="w-4 h-4" />
                        <span className="text-sm">Not linked</span>
                      </div>
                    )}

                    {/* Recruiting Status */}
                    {account.dive_center_profile?.recruiting && (
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                        Recruiting
                      </span>
                    )}

                    {/* Link Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLinkingAccount(account)}
                    >
                      <LinkIcon className="w-4 h-4 mr-1" />
                      {account.linked_dive_center_id ? "Change Link" : "Link"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Link Modal */}
      {linkingAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Link to Dive Center</CardTitle>
                <button onClick={() => setLinkingAccount(null)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Linking <strong>{linkingAccount.name}</strong> to:
                </p>
                <Label>Select Dive Center</Label>
                <select
                  value={selectedCenterId}
                  onChange={(e) => setSelectedCenterId(e.target.value)}
                  className="w-full mt-1 p-2 border rounded-lg"
                >
                  <option value="">-- Select a dive center --</option>
                  {diveCenters.map((center) => (
                    <option key={center.id} value={center.id}>
                      {center.name} - {center.location}
                    </option>
                  ))}
                </select>
              </div>

              {selectedCenterId && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  {(() => {
                    const center = diveCenters.find(c => c.id === selectedCenterId);
                    return center ? (
                      <div className="flex items-center gap-3">
                        <Building2 className="w-8 h-8 text-blue-600" />
                        <div>
                          <p className="font-medium">{center.name}</p>
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {center.location}{center.country && `, ${center.country}`}
                          </p>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              <Button
                onClick={handleLinkCenter}
                disabled={!selectedCenterId}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Link Account
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DiveCenterAccountsManager;
